import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import React from 'react';
import { partnerApi } from '../../lib/api';
import { BatchErrors } from './BatchErrors';
import { BatchStatusBadge } from './BatchStatusBadge';

interface BatchDetailModalProps {
  batchId: string;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
}

const getBatchTypeLabel = (batchType: string): string => {
  switch (batchType) {
    case 'PARTNER_RESOURCE_JSON_INGEST':
      return 'Data Import';
    case 'PARTNER_RESOURCE_BATCH_DELETE':
      return 'Bulk Delete';
    case 'PARTNER_RESOURCE_BATCH_PATCH':
      return 'Bulk Update';
    default:
      return batchType;
  }
};

export const BatchDetailModal: React.FC<BatchDetailModalProps> = ({
  batchId,
  tenantId,
  isOpen,
  onClose,
}) => {
  const {
    data: batch,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['batch-status', batchId, tenantId],
    queryFn: () => partnerApi.getBatchStatus(batchId, tenantId),
    enabled: isOpen && !!batchId && !!tenantId,
    retry: false,
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Batch Details</h2>
              <p className="text-sm text-gray-500 mt-1">Batch ID: {batchId}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="ml-2 text-sm text-gray-500">Loading batch details...</span>
              </div>
            ) : error ? (
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-red-800">Error loading batch</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error instanceof Error ? error.message : 'Batch not found'}
                </div>
              </div>
            ) : batch ? (
              <div className="space-y-6">
                {/* Overview Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Overview</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <div className="mt-1">
                        <BatchStatusBadge status={batch.status} />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Batch Type</label>
                      <p className="mt-1 text-sm text-gray-900">{getBatchTypeLabel(batch.batchType)}</p>
                    </div>
                    {batch.completedAt && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Completed At</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {new Date(batch.completedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Progress</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Total Records</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          {batch.progress.totalRecords ?? 0}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Processed</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          {batch.progress.processedRecords ?? 0}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Successful</label>
                        <p className="mt-1 text-lg font-semibold text-green-600">
                          {batch.progress.successfulRecords ?? 0}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Failed</label>
                        <p className="mt-1 text-lg font-semibold text-red-600">
                          {batch.progress.failedRecords ?? 0}
                        </p>
                      </div>
                    </div>
                    {batch.progress.totalRecords && batch.progress.totalRecords > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">Success Rate</span>
                          <span className="text-sm font-medium text-gray-700">
                            {batch.progress.successRate?.toFixed(1) ?? 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full transition-all"
                            style={{
                              width: `${batch.progress.successRate ?? 0}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {batch.errors && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Errors</h3>
                    <BatchErrors errors={batch.errors} />
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
